<template>
  <div>
    <v-app-bar flat color="transparent" class="main-appbar">
      <app-bar-nav-icon />
      <v-skeleton-loader
        type="chip"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <v-toolbar-title v-else class="headline font-weight-bold"
        >Banner and Social Media</v-toolbar-title
      >

      <v-spacer></v-spacer>

      <v-row v-show="showSearch" align="center" style="max-width: 300px">
        <v-text-field
          class="search"
          ref="searchField"
          flat
          hide-details
          solo
          rounded
          clearable
          background-color="grey lighten-3"
          label="Search Category"
          v-model="filter.search"
          @input="search"
        ></v-text-field>
      </v-row>

      <v-skeleton-loader
        type="actions"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <template v-else>
        <v-select
          class="ml-10"
          hide-details
          dense
          item-text="label"
          item-value="value"
          v-model="defaultFilter"
          :items="filterBy"
          style="max-width: 200px"
          @change="filterBanner"
        />

        <v-btn
          depressed
          class="ml-2"
          :color="sortOrder ? 'primary' : ''"
          @click="
            sortOrder = true
            filterBanner()
          "
        >
          <v-icon>{{ icons.arrowUp }}</v-icon>
        </v-btn>
        <v-btn
          depressed
          class="ml-2"
          :color="!sortOrder ? 'primary' : ''"
          @click="
            sortOrder = false
            filterBanner()
          "
        >
          <v-icon>{{ icons.arrowDown }}</v-icon>
        </v-btn>

        <v-btn class="ml-5" icon @click="showSearchField">
          <img src="@/assets/icons/search.svg" alt height="17.7px" />
        </v-btn>
      </template>
    </v-app-bar>

    <div
      class="mx-4"
      id="venue-list"
      v-infinite-scroll="loadMore"
      :infinite-scroll-disabled="loading"
      infinite-scroll-distance="100"
    >
      <div class="d-flex my-6">
        <v-spacer></v-spacer>
        <template v-if="!loading">
          <v-btn
            color="primary"
            @click="$router.push({ name: 'banners.create' })"
            ><v-icon left dark> {{ icons.add }} </v-icon>Add Banner And Social
            Media</v-btn
          >
        </template>
      </div>
      <v-simple-table>
        <thead>
          <tr>
            <th width="15%" class="text-left">Title</th>
            <th width="15%" class="text-left">Type</th>
            <th width="15%" class="text-left">Url</th>
            <th width="15%" class="text-left">Date Created</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="banner in banners"
            :key="banner.id"
            class="clickable"
            @click="
              $router.push({
                name: 'banners.details',
                params: { id: banner.id },
              })
            "
          >
            <td>{{ banner.title }}</td>
            <td>{{ banner.type }}</td>
            <td>{{ banner.url }}</td>
            <td>{{ banner.addedDate }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import {
  mdiDotsVertical,
  mdiUnfoldMoreHorizontal,
  mdiMagnify,
  mdiAccountCircleOutline,
  mdiChevronRight,
  mdiFileExcel,
  mdiPlus,
  mdiArrowUp,
  mdiArrowDown,
} from '@mdi/js'
import ControlsMixin from '@/utils/mixins/Controls'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import infiniteScroll from 'vue-infinite-scroll'
import debounce from 'lodash/debounce'

export default {
  name: 'Banner',

  mixins: [ControlsMixin],

  directives: {
    infiniteScroll,
  },

  components: {
    AppBarNavIcon,
  },

  data() {
    return {
      loading: false,
      showSearch: false,
      filter: {
        search: '',
      },
      icons: {
        settings: mdiDotsVertical,
        sort: mdiUnfoldMoreHorizontal,
        search: mdiMagnify,
        view: mdiAccountCircleOutline,
        arrow: mdiChevronRight,
        export: mdiFileExcel,
        add: mdiPlus,
        arrowUp: mdiArrowUp,
        arrowDown: mdiArrowDown,
      },
      snackbar: {
        show: false,
        message: null,
        color: '',
      },
      defaultFilter: 'title',
      sortOrder: true,
      filterBy: [
        {
          value: 'title',
          label: 'Title',
        },
        {
          value: 'type',
          label: 'Type',
        },
        {
          value: 'url',
          label: 'URL',
        },
        {
          value: 'created_at',
          label: 'Date Created',
        },
      ],
    }
  },

  created() {
    this.clearBannerList()
  },

  computed: {
    ...mapState({
      banners: (state) => state.banner.list,
      listMeta: (state) => state.banner.listMeta,
    }),

    paginationLength() {
      return (this.listMeta.total / this.listMeta.per_page) | 0
    },
  },

  mounted() {
    this.fetchBanner()
  },

  methods: {
    ...mapActions({
      getBanners: 'banner/getBanners',
    }),

    ...mapMutations({
      clearBannerList: 'banner/clearBannerList',
    }),

    pageChanged(page) {
      this.clearBannerList()
      this.fetchBanner(page)
    },

    filterBanner(filterValue) {
      this.clearBannerList()
      this.fetchBanner(null, filterValue)
    },

    loadMore() {
      if (
        this.listMeta &&
        this.listMeta.current_page < this.listMeta.last_page
      ) {
        this.fetchBanner(this.listMeta.current_page + 1)
      }
    },

    search: debounce(function () {
      this.clearBanner()
      this.fetchBanner()

      setTimeout(() => {
        this.$refs.searchField.$refs.input.focus()
      }, 500)
    }, 1600),

    async fetchBanner(page = 1) {
      if (this.loading) return
      let params = {
        page,
        sort: this.sortOrder
          ? `${this.defaultFilter}`
          : `-${this.defaultFilter}`,
      }
      if (this.filter.search) {
        params.search = this.filter.search
      }
      this.loading = true
      await this.getBanners(params)
      this.loading = false
    },

    async showSearchField() {
      this.showSearch = !this.showSearch
      await this.$nextTick()
      this.$refs.searchField.$refs.input.focus()
    },
  },
}
</script>
